import type { MarketplaceBrowseMapMarkers } from 'components/marketplace/browse/marketplaceBrowse';
import { browseListingStatus } from 'components/marketplace/helpers/browseListingStatus';
import type { LikedPropertyIds } from 'components/marketplace/types';
import type {
  CircleLayerSpecification,
  GeoJSONFeature,
  Map as MapboxMap,
  MapMouseEvent,
  SymbolLayerSpecification,
} from 'mapbox-gl';

import {
  getMapMarkerImageWidth,
  MAP_MARKER_COLORS,
  MAP_MARKER_HEIGHT,
  mapMarkerImageId,
} from './mapMarkerImage';
import type { MapMarkerVariant } from './mapMarkerImage';

export const MARKETPLACE_MARKER_SOURCE_ID = 'marketplace-map-markers';
export const MARKETPLACE_MARKER_LAYER_ID = 'marketplace-map-marker-symbols';
export const MARKETPLACE_MARKER_HIGHLIGHT_LAYER_ID = 'marketplace-map-marker-highlighted-symbol';
export const MARKETPLACE_MARKER_DOT_LAYER_ID = 'marketplace-map-marker-dots';
export const MARKETPLACE_MARKER_HALO_LAYER_ID = 'marketplace-map-marker-halos';

const MARKER_HALO_RADII = {
  listed: 6.9,
  'self-tour': 7.4,
  'coming-soon': 7,
} satisfies Record<MapMarkerVariant, number>;

let coarsePointerQuery: MediaQueryList | undefined;

function hasCoarsePointer() {
  if (typeof window === 'undefined') {
    return false;
  }
  coarsePointerQuery ??= window.matchMedia('(pointer: coarse)');
  return coarsePointerQuery.matches;
}

type MapMarkerProperties = {
  addressId: string;
  focusedImageId: string;
  highlightedImageId: string;
  imageId: string;
  isLiked: boolean;
  priority: number;
  variant: MapMarkerVariant;
};

function formatMapMarkerPrice(number: number) {
  return number.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: number >= 1_000_000 ? 2 : 0,
    notation: 'compact',
    compactDisplay: 'short',
  });
}

type MarkerLabelOptions = {
  isLoading: boolean;
  listPriceCents: number | null;
  /** Marketplace listing state used when a price is not yet public. */
  listingState?: string | null;
};

function markerLabel({ isLoading, listPriceCents, listingState }: MarkerLabelOptions) {
  if (isLoading) {
    return '•••';
  }
  if (listPriceCents != null) {
    return formatMapMarkerPrice(listPriceCents / 100);
  }
  return listingState === 'AVAILABLE_SOON' ? 'Soon' : '';
}

type BuildMapMarkerFeatureCollectionOptions = {
  loadingAddressId?: string | null;
  likedPropertyIds?: LikedPropertyIds;
  markers?: MarketplaceBrowseMapMarkers | null;
  selectedAddressId?: string;
};

export function buildMapMarkerFeatureCollection({
  loadingAddressId,
  likedPropertyIds,
  markers,
  selectedAddressId,
}: BuildMapMarkerFeatureCollectionOptions) {
  const activeAddressId = loadingAddressId ?? selectedAddressId;
  // oxlint-disable-next-line unicorn/no-array-sort -- Cosmos supports browsers without Array.prototype.toSorted.
  const sortedMarkers = [...(markers ?? [])].sort(
    (a, b) => b.location.latitude - a.location.latitude || a.addressId.localeCompare(b.addressId),
  );

  return {
    type: 'FeatureCollection' as const,
    features: sortedMarkers.map((marker) => {
      const isLiked = likedPropertyIds?.has(marker.addressId) ?? false;
      const isLoading = marker.addressId === loadingAddressId;
      const isHighlighted = marker.addressId === activeAddressId;
      const label = markerLabel({
        isLoading,
        listingState: marker.listingState,
        listPriceCents: marker.listPriceCents,
      });
      const variant = browseListingStatus(marker.displayBadge) ?? 'listed';
      const statusPriority = variant === 'self-tour' ? 1 : 2;
      return {
        type: 'Feature' as const,
        id: marker.addressId,
        geometry: {
          type: 'Point' as const,
          coordinates: [marker.location.longitude, marker.location.latitude],
        },
        properties: {
          addressId: marker.addressId,
          focusedImageId: mapMarkerImageId({
            hasFocusRing: true,
            isHighlighted: true,
            isLiked,
            isLoading,
            label,
            variant,
          }),
          highlightedImageId: mapMarkerImageId({
            hasFocusRing: false,
            isHighlighted: true,
            isLiked,
            isLoading,
            label,
            variant,
          }),
          imageId: mapMarkerImageId({
            hasFocusRing: false,
            isHighlighted,
            isLiked,
            isLoading,
            label,
            variant,
          }),
          isLiked,
          priority: isLiked ? 0 : statusPriority,
          variant,
        } satisfies MapMarkerProperties,
      };
    }),
  };
}

export const marketplaceMarkerHaloLayer: CircleLayerSpecification = {
  id: MARKETPLACE_MARKER_HALO_LAYER_ID,
  type: 'circle',
  source: MARKETPLACE_MARKER_SOURCE_ID,
  paint: {
    'circle-emissive-strength': 1,
    'circle-pitch-scale': 'viewport',
    'circle-radius': [
      'match',
      ['get', 'variant'],
      'self-tour',
      MARKER_HALO_RADII['self-tour'],
      'coming-soon',
      MARKER_HALO_RADII['coming-soon'],
      MARKER_HALO_RADII.listed,
    ],
    'circle-color': MAP_MARKER_COLORS.white,
    'circle-opacity': 0.95,
  },
};

export const marketplaceMarkerDotLayer: CircleLayerSpecification = {
  id: MARKETPLACE_MARKER_DOT_LAYER_ID,
  type: 'circle',
  source: MARKETPLACE_MARKER_SOURCE_ID,
  paint: {
    'circle-emissive-strength': 1,
    'circle-pitch-scale': 'viewport',
    'circle-radius': ['match', ['get', 'variant'], 'self-tour', 3.2, 4.6],
    'circle-color': [
      'match',
      ['get', 'variant'],
      'self-tour',
      MAP_MARKER_COLORS.white,
      'coming-soon',
      MAP_MARKER_COLORS.white,
      MAP_MARKER_COLORS.listed,
    ],
    'circle-stroke-color': [
      'match',
      ['get', 'variant'],
      'self-tour',
      MAP_MARKER_COLORS.selfTour,
      'coming-soon',
      MAP_MARKER_COLORS.comingSoon,
      MAP_MARKER_COLORS.white,
    ],
    'circle-stroke-width': ['match', ['get', 'variant'], 'self-tour', 3.6, 'coming-soon', 1.9, 1.8],
  },
};

export const marketplaceMarkerLayer: SymbolLayerSpecification = {
  id: MARKETPLACE_MARKER_LAYER_ID,
  type: 'symbol',
  source: MARKETPLACE_MARKER_SOURCE_ID,
  layout: {
    'symbol-placement': 'point',
    'symbol-z-order': 'source',
    'symbol-sort-key': ['get', 'priority'],
    'icon-image': ['get', 'imageId'],
    'icon-allow-overlap': false,
    'icon-ignore-placement': false,
    'icon-padding': 3,
    'icon-anchor': 'center',
    'icon-rotation-alignment': 'viewport',
  },
};

export const marketplaceHighlightedMarkerLayer: SymbolLayerSpecification = {
  ...marketplaceMarkerLayer,
  id: MARKETPLACE_MARKER_HIGHLIGHT_LAYER_ID,
  layout: {
    ...marketplaceMarkerLayer.layout,
    'icon-image': ['get', 'highlightedImageId'],
    'icon-allow-overlap': true,
  },
};

type MarkerQueryMap = Pick<MapboxMap, 'getLayer' | 'project' | 'queryRenderedFeatures'>;

type MarkerFeature = GeoJSONFeature & {
  geometry?: {
    coordinates?: unknown;
    type?: string;
  } | null;
  id?: string | number;
  properties?: Record<string, unknown> | null;
};

type MarkerPointOptions = {
  feature: MarkerFeature;
  map: MarkerQueryMap;
};

function markerPoint({ feature, map }: MarkerPointOptions) {
  if (feature.geometry?.type !== 'Point') {
    return null;
  }
  const coordinates = feature.geometry.coordinates;
  if (!Array.isArray(coordinates)) {
    return null;
  }
  const longitude: unknown = coordinates[0];
  const latitude: unknown = coordinates[1];
  if (
    typeof longitude !== 'number' ||
    !Number.isFinite(longitude) ||
    typeof latitude !== 'number' ||
    !Number.isFinite(latitude) ||
    latitude < -90 ||
    latitude > 90
  ) {
    return null;
  }
  return map.project([longitude, latitude]);
}

type MarkerHitOptions = MarkerPointOptions & {
  event: MapMouseEvent;
  hitSlop?: number;
};

function isCircularMarker(feature: MarkerFeature) {
  return (
    feature.layer?.id === MARKETPLACE_MARKER_DOT_LAYER_ID ||
    feature.layer?.id === MARKETPLACE_MARKER_HALO_LAYER_ID
  );
}

function isMarkerHit({ event, feature, map, hitSlop = 0 }: MarkerHitOptions) {
  const center = markerPoint({ feature, map });
  if (!center) {
    return false;
  }
  const distanceX = Math.abs(event.point.x - center.x);
  const distanceY = Math.abs(event.point.y - center.y);
  if (isCircularMarker(feature)) {
    const variant = feature.properties?.variant;
    const radius =
      variant === 'self-tour' || variant === 'coming-soon'
        ? MARKER_HALO_RADII[variant]
        : MARKER_HALO_RADII.listed;
    return distanceX * distanceX + distanceY * distanceY <= (radius + hitSlop) ** 2;
  }
  const imageId = feature.properties?.imageId;
  const width =
    typeof imageId === 'string' ? getMapMarkerImageWidth({ id: imageId, map }) : undefined;
  if (width == null) {
    return false;
  }
  const halfWidth = width / 2 + hitSlop;
  const halfHeight = MAP_MARKER_HEIGHT / 2 + hitSlop;
  if (distanceX > halfWidth || distanceY > halfHeight) {
    return false;
  }
  const cornerX = Math.max(0, distanceX - (halfWidth - halfHeight));
  return cornerX * cornerX + distanceY * distanceY <= halfHeight * halfHeight;
}

function featureAddressId(feature: MarkerFeature) {
  const addressId: unknown = feature.properties?.addressId ?? feature.id;
  return typeof addressId === 'string' ? addressId : null;
}

type MarketplaceMarkerAddressIdAtPointOptions = {
  event: MapMouseEvent;
  map: MarkerQueryMap;
};

export function marketplaceMarkerAddressIdAtPoint({
  event,
  map,
}: MarketplaceMarkerAddressIdAtPointOptions) {
  const layers = [
    MARKETPLACE_MARKER_HIGHLIGHT_LAYER_ID,
    MARKETPLACE_MARKER_LAYER_ID,
    MARKETPLACE_MARKER_DOT_LAYER_ID,
    MARKETPLACE_MARKER_HALO_LAYER_ID,
  ].filter((layerId) => map.getLayer(layerId));
  if (layers.length === 0) {
    return null;
  }
  const features = map.queryRenderedFeatures(event.point, { layers });
  // An active price can cover a nearby dot. Prefer the closest directly hit home.
  let nearestDotAddressId: string | null = null;
  let nearestDotDistance = Number.POSITIVE_INFINITY;
  for (const feature of features) {
    if (!isCircularMarker(feature)) {
      continue;
    }
    const point = markerPoint({ feature, map });
    const addressId = featureAddressId(feature);
    if (!point || !addressId || !isMarkerHit({ event, feature, map })) {
      continue;
    }
    const distance = Math.hypot(point.x - event.point.x, point.y - event.point.y);
    if (distance < nearestDotDistance) {
      nearestDotDistance = distance;
      nearestDotAddressId = addressId;
    }
  }
  if (nearestDotAddressId) {
    return nearestDotAddressId;
  }
  for (const feature of features) {
    if (isMarkerHit({ event, feature, map })) {
      const addressId = featureAddressId(feature);
      if (addressId) {
        return addressId;
      }
    }
  }
  if (hasCoarsePointer()) {
    const radius = 23;
    const nearby = map.queryRenderedFeatures(
      [
        [event.point.x - radius, event.point.y - radius],
        [event.point.x + radius, event.point.y + radius],
      ],
      { layers },
    );
    let nearestAddressId: string | null = null;
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (const feature of nearby) {
      const point = markerPoint({ feature, map });
      const addressId = featureAddressId(feature);
      if (!point || !addressId || !isMarkerHit({ event, feature, map, hitSlop: 16 })) {
        continue;
      }
      const distance = Math.hypot(point.x - event.point.x, point.y - event.point.y);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestAddressId = addressId;
      }
    }
    return nearestAddressId;
  }
  return null;
}
